import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Modal,
  notification,
  Form,
  message,
} from "lib/ui";
import { PlusOutlined, DeleteOutlined } from "lib/icons";
import { Store, Users, Wallet, Percent, Pencil, Trash2 } from "lucide-react";
import AddUserForm from "./Add";
import ApiPath from "../../../hooks/ApiFetch";
import {
  AdminPageLayout,
  AdminHero,
  AdminStats,
  AdminSearchBar,
} from "../../../components/admin/AdminPageShell";
import {
  PeopleDataTable,
  ActionBtn,
  PersonCell,
} from "../../../components/admin/PeopleDataTable";

const VendorsPage = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await ApiPath.get("/vendors");
      setData(Array.isArray(response.data) ? response.data : []);
    } catch {
      message.error("Failed to load vendors");
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue(user);
    setIsModalVisible(true);
  };

  const handleDelete = (id, title) => {
    Modal.confirm({
      title: "Delete vendor?",
      icon: <DeleteOutlined />,
      content: title ? `Remove ${title}` : undefined,
      okText: "Delete",
      cancelText: "Cancel",
      okType: "danger",
      onOk: async () => {
        try {
          await ApiPath.delete(`/vendors/${id}`);
          setData((prev) => prev.filter((item) => item._id !== id));
          notification.success({
            message: "Deleted",
            description: "Vendor removed.",
          });
        } catch {
          notification.error({
            message: "Failed",
            description: "Could not delete vendor.",
          });
        }
      },
    });
  };

  const handleSave = async (values) => {
    try {
      if (editingUser) {
        await ApiPath.put(`/vendors/${editingUser._id}`, values);
        setData((prev) =>
          prev.map((item) =>
            item._id === editingUser._id ? { ...item, ...values } : item
          )
        );
        notification.success({
          message: "Updated",
          description: "Vendor saved.",
        });
      } else {
        const response = await ApiPath.post("/vendors", values);
        setData((prev) => [
          ...prev,
          { ...values, _id: response.data._id ?? response.data?._id },
        ]);
        notification.success({
          message: "Added",
          description: "Vendor created.",
        });
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch {
      notification.error({
        message: "Failed",
        description: "Could not save vendor.",
      });
    }
  };

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((item) => {
      const blob = [
        item.name,
        item.email,
        item.mobile_number,
        item.CommisionType,
        item.CommisionUnit,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [data, search]);

  const totalBalance = filteredData.reduce(
    (s, v) => s + Number(v.userBalance ?? 0),
    0
  );

  const columns = [
    {
      key: "vendor",
      header: "Vendor",
      render: (row) => (
        <PersonCell name={row.name} email={row.email} subtitle={row.mobile_number} />
      ),
    },
    {
      key: "mobile",
      header: "Mobile",
      render: (row) => (
        <span className="font-mono text-sm">{row.mobile_number || "—"}</span>
      ),
    },
    {
      key: "commission",
      header: "Commission",
      render: (row) => (
        <span className="text-sm text-slate-700">
          {row.CommisionType || "—"}
          {row.CommisionUnit != null ? ` · ${row.CommisionUnit}` : ""}
        </span>
      ),
    },
    {
      key: "balance",
      header: "Balance",
      align: "right",
      render: (row) => (
        <span className="tabular-nums font-semibold text-slate-900">
          {Number(row.userBalance ?? 0).toFixed(2)}{" "}
          <span className="font-normal text-slate-400">AED</span>
        </span>
      ),
    },
  ];

  return (
    <AdminPageLayout>
      <AdminHero
        icon={Store}
        badge="Partners"
        title="Vendors"
        description="Manage vendor accounts, commission rules, and payout balances."
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            className="!border-0 !bg-white !text-brand-dark hover:!bg-violet-50"
          >
            Add vendor
          </Button>
        }
      />

      <AdminStats
        items={[
          { label: "Vendors", value: data.length, icon: Store },
          { label: "Showing", value: filteredData.length, icon: Users },
          {
            label: "Balance (filtered)",
            value: `${totalBalance.toFixed(2)} AED`,
            icon: Wallet,
          },
          { label: "With commission", value: filteredData.filter((v) => v.CommisionType).length, icon: Percent },
        ]}
      />

      <AdminSearchBar
        placeholder="Search name, email, mobile, commission…"
        value={search}
        onChange={setSearch}
      />

      <PeopleDataTable
        columns={columns}
        rows={filteredData}
        rowKey="_id"
        renderActions={(row) => (
          <>
            <ActionBtn variant="edit" onClick={() => handleEdit(row)}>
              <Pencil size={14} />
              Edit
            </ActionBtn>
            <ActionBtn
              variant="delete"
              onClick={() => handleDelete(row._id, row.name)}
            >
              <Trash2 size={14} />
              Delete
            </ActionBtn>
          </>
        )}
        emptyTitle="No vendors found"
        emptyHint="Add a vendor or clear your search."
      />

      <Modal
        title={editingUser ? "Edit vendor" : "Add vendor"}
        visible={isModalVisible}
        onCancel={() => {
          form.resetFields();
          setIsModalVisible(false);
        }}
        footer={null}
        width={800}
      >
        <AddUserForm
          form={form}
          onFinish={handleSave}
          initialValues={editingUser || {}}
        />
      </Modal>
    </AdminPageLayout>
  );
};

export default VendorsPage;
